/**
 * §9.13 — the Hold meter and `passive.ballast-holdfast` (AMD-39, threat measure AMD-40).
 *
 * The ship that does not move gets paid, but only while something is close enough to kill it.
 * Two independent factors multiply into one payout scalar:
 *
 *  - **meter** (0..1) — filled by not moving, drained three times faster by moving, shed
 *    outright by a dash. Purely kinematic: it reads the ship's actual displacement this tick,
 *    so knockback, stun, dash and stick input all land in the same number.
 *  - **threat** (0..1) — the weight of everything ARRIVING inside the §5.8 acquisition ring:
 *    each body scaled by the larger of its closing rate and its occupancy of the contact
 *    band, then normalised. Counting mere presence was measured and inverted the design
 *    (AMD-39): a fleeing player's ring is full of a receding tail, so presence paid the
 *    mover and starved the stander. Counting arrival collapses the tail and leaves the
 *    stander untouched (AMD-40). An empty ring is exactly zero however long you have stood
 *    there, which is the rule that stops this being an idle mechanic.
 *
 * `payout = meter × threat`, and every reward axis is `rank_value × payout`. The line is
 * self-limiting by construction rather than by a tuning constant: weapons auto-fire, so you
 * can hold exactly as long as your DPS clears the ring faster than the ring closes, and the
 * same density that pays the most is the density that kills you.
 *
 * Determinism: no RNG on any stream, no wall clock, no allocation after module load. The
 * threat query reuses the world's spatial hash and its seen-stamp dedupe; the ring scan is
 * one `queryCircle` per tick for the whole sim (§14.4).
 *
 * Step order: `stepHold` is called from the head of `stepProgression` (step 13), so the
 * modifiers computed on tick N are read by weapons (step 8) and damage on tick N+1. The
 * one-tick lag is fixed, deterministic and below the 150 ms input-buffer window (§3.6).
 */

import { cfg } from '../core/config';
import { STEP } from '../core/loop';
import { PASSIVES, bossById } from '../core/content';
import type { World } from './world';
import { POOL_CAPACITY } from './world';
import { wrapDeltaX, wrapDeltaY, wrapDist2 } from './wrap';
import { getPlayerRuntime } from './player';
import {
  CAP_CRIT_CHANCE,
  CAP_HASTE,
  CAP_INCREASED_DAMAGE,
  CAP_MITIGATION,
  buildBaseOf,
  buildExtras,
  passiveRank,
} from './build';
import { STATE_COMMITTED, STATE_WINDING } from './enemies';
import { grantIchor, trashCentiAtTier } from './gacha';

/** §9.13 — the one passive that reads the Hold meter. */
export const HOLD_PASSIVE_ID = 'passive.ballast-holdfast';

/** Centi-ichor per whole ichor, mirroring §9.12.2's integer-hundredths convention. */
const CENTI = 100;

/**
 * The meter is a sum of 60 f64 increments per second, so "full" lands a few ULPs short of
 * 1 on the exact tick the ramp completes. `engaged` reads through this tolerance rather
 * than testing `>= 1`, which would make the anchor cue fire a tick late and only sometimes.
 */
const ENGAGE_EPS = 1e-6;

/**
 * One row of the §6.1 absolute set-value table. Every number is the value **at full hold
 * and full threat**; the live bonus is the row scaled by `payout`.
 */
export interface HoldRank {
  /** Added to the §4.2 increased-damage ADD bucket, in percentage points. */
  damage_pct: number;
  /** Added to the §4.6 haste ADD bucket, in percentage points. */
  attack_speed_pct: number;
  /** Added to crit chance, in percentage points. */
  crit_chance_pct: number;
  /** Added to the §4.4 mitigation fraction, in percentage points. */
  armor_pct: number;
  /**
   * Multiplier ADDED to `pickup_radius_mult` while anchored (§10.9 mote magnet).
   *
   * The measurement that put this axis here: a stationary ship kills out to the 18 u ring
   * and collects inside 3.2 u, so an anchored pilot at 190 kills was still level 1 — it was
   * earning XP it physically could not reach, and with it the Lumensurge (§9.5) and the
   * surge-scaled ichor (§9.12.2) that XP feeds. The Holdfast does not chase its food; it
   * draws it in. Applied OUTSIDE the §4.2 ADD bucket and its cap, because it is a
   * conditional multiplier and not a passive stacking into that bucket — and because it
   * returns to zero the tick the ship moves.
   */
  mote_draw_mult: number;
  /** Bonus on §9.12.2's trash ichor trickle, in percent of the base credit. */
  ichor_pct: number;
}

function zeroRank(): HoldRank {
  return { damage_pct: 0, attack_speed_pct: 0, crit_chance_pct: 0, armor_pct: 0, mote_draw_mult: 0, ichor_pct: 0 };
}

function readNum(row: Record<string, unknown>, key: string): number {
  const v = row[key];
  return typeof v === 'number' ? v : 0;
}

/** Content-driven rank table, built once at import time. Index 0 is rank 1. */
const RANKS: HoldRank[] = [];

(function loadRankTable(): void {
  const rows = PASSIVES as unknown as { id: string; max_rank?: number; rank_table?: Record<string, unknown>[] }[];
  for (let i = 0; i < rows.length; i++) {
    if (rows[i].id !== HOLD_PASSIVE_ID) continue;
    const table = rows[i].rank_table;
    if (table === undefined) break;
    for (let r = 0; r < table.length; r++) {
      RANKS.push({
        damage_pct: readNum(table[r], 'damage_pct'),
        attack_speed_pct: readNum(table[r], 'attack_speed_pct'),
        crit_chance_pct: readNum(table[r], 'crit_chance_pct'),
        armor_pct: readNum(table[r], 'armor_pct'),
        mote_draw_mult: readNum(table[r], 'mote_draw_mult'),
        ichor_pct: readNum(table[r], 'ichor_pct'),
      });
    }
    break;
  }
})();

/**
 * Measurement hook (`tools/outrun-probe.ts`). Scales the applied bonus so a sweep can ask
 * whether the payout MAGNITUDE is the binding constraint or the encounter rate is. Ships
 * at 1 and is not reachable from the UI — the same shape as `setHuntRampEnabled` (AMD-24c).
 */
let payoutScale = 1;
export function setHoldPayoutScale(x: number): void {
  payoutScale = x;
}

/** Highest rank the content table defines (5 per §6.1). */
export const HOLD_MAX_RANK = RANKS.length;

/** The §6.1 set-values at `rank`, or an all-zero row outside 1..HOLD_MAX_RANK. */
export function holdRankValues(rank: number): Readonly<HoldRank> {
  if (rank < 1 || rank > RANKS.length) return EMPTY_RANK;
  return RANKS[rank - 1];
}

const EMPTY_RANK: Readonly<HoldRank> = zeroRank();

// --- per-world state ---

export interface HoldState {
  /** 0..1 meter fill. */
  meter: number;
  /** 0..1 normalised threat inside the acquisition ring. */
  threat: number;
  /** Raw summed threat weight inside the ring, under the selected measure. */
  weight: number;
  /** All four candidate measures, computed in the same pass (AMD-40). */
  presence: number;
  closing: number;
  band: number;
  arrive: number;
  /** Bodies counted inside the ring (deduped, dying excluded). */
  count: number;
  /** `meter × threat` — the single scalar every reward axis multiplies. */
  payout: number;
  /** Owned rank, 0 when the line is not drafted. */
  rank: number;
  /** True while the meter is full; drives the §12 cue and the §13 HUD state. */
  engaged: boolean;
  /** Live bonus, already scaled by `payout`. */
  bonus: HoldRank;
  /** Fractional trash-ichor bonus banked below the next whole unit (§9.12.2). */
  ichorCenti: number;
  /** Bus unsubscribe for the ichor hook; null while uninstalled. */
  unsub: (() => void) | null;
}

const STATES = new WeakMap<World, HoldState>();

function stateOf(w: World): HoldState {
  let st = STATES.get(w);
  if (st === undefined) {
    st = {
      meter: 0,
      threat: 0,
      weight: 0,
      presence: 0,
      closing: 0,
      band: 0,
      arrive: 0,
      count: 0,
      payout: 0,
      rank: 0,
      engaged: false,
      bonus: zeroRank(),
      ichorCenti: 0,
      unsub: null,
    };
    STATES.set(w, st);
  }
  return st;
}

/**
 * Candidate buffer for the ring query. Allocated once at module load and reused by every
 * world — `stepHold` is synchronous and never yields, so a shared buffer cannot alias.
 */
const ringBuf = new Int32Array(POOL_CAPACITY.enemies + 64);

/** Preallocated `hold_change` payloads; a small ring so two emits in one tick cannot alias. */
const EVENT_RING = 4;
const holdPayloads: { engaged: boolean; meter: number; payout: number; threat: number; cause: string }[] =
  new Array(EVENT_RING);
for (let i = 0; i < EVENT_RING; i++) {
  holdPayloads[i] = { engaged: false, meter: 0, payout: 0, threat: 0, cause: '' };
}
let holdCursor = 0;

function emitHold(w: World, st: HoldState, cause: 'anchored' | 'moved' | 'dashed'): void {
  const ev = holdPayloads[holdCursor];
  holdCursor = (holdCursor + 1) % EVENT_RING;
  ev.engaged = st.engaged;
  ev.meter = st.meter;
  ev.payout = st.payout;
  ev.threat = st.threat;
  ev.cause = cause;
  w.bus.emit('hold_change', ev);
}

// --- ichor (§9.12.2 trickle bonus) ---

/**
 * The ichor axis is a **modifier on §9.12.2's kill income**, not a second source: it fires
 * only on a kill, only on the trash trickle §9.12.2 already scales by Lumensurge, and only
 * while the ring is crowded and the ship is still. AMD-39 measures the stacking.
 */
function onDeath(w: World, payload: { elite: boolean; def_id: string }): void {
  const st = STATES.get(w);
  if (st === undefined || st.rank <= 0) return;
  const pct = st.bonus.ichor_pct;
  if (pct <= 0) return;
  if (payload.elite || bossById.has(payload.def_id)) return;
  st.ichorCenti += (trashCentiAtTier(w.player.lumensurge_tier) * pct) / 100;
  if (st.ichorCenti < CENTI) return;
  const whole = Math.floor(st.ichorCenti / CENTI);
  st.ichorCenti -= whole * CENTI;
  grantIchor(w, whole);
}

/** Subscribes the ichor hook to `death`. Idempotent: a second call replaces the first. */
export function installHold(w: World): void {
  const st = stateOf(w);
  if (st.unsub !== null) st.unsub();
  st.unsub = w.bus.on('death', (p) => onDeath(w, p));
}

/** Clears the meter and the banked ichor fraction. Call alongside `world.reset(seed)`. */
export function resetHold(w: World): void {
  const st = stateOf(w);
  st.meter = 0;
  st.threat = 0;
  st.weight = 0;
  st.presence = 0;
  st.closing = 0;
  st.band = 0;
  st.arrive = 0;
  st.count = 0;
  st.payout = 0;
  st.rank = 0;
  st.engaged = false;
  st.ichorCenti = 0;
  clearBonus(st);
}

function clearBonus(st: HoldState): void {
  st.bonus.damage_pct = 0;
  st.bonus.attack_speed_pct = 0;
  st.bonus.crit_chance_pct = 0;
  st.bonus.armor_pct = 0;
  st.bonus.mote_draw_mult = 0;
  st.bonus.ichor_pct = 0;
}

// --- the meter ---

/**
 * Displacement this tick, in u/s, measured on the torus (AMD-24). `prevX/prevY` is the
 * position at the head of step 2, so this catches the movement model, the dash, knockback
 * and a stun in one number and needs no special case for any of them.
 */
function playerSpeedThisTick(w: World): number {
  const rt = getPlayerRuntime(w);
  const p = w.player;
  return Math.sqrt(wrapDist2(rt.prevX, rt.prevY, p.pos.x, p.pos.y)) / STEP;
}

/**
 * §9.13.2 — the still/moving crossfade. Returns the signed meter rate per second.
 *
 * At or below `hold.still_speed_u` (0.8 u/s, 10% of top speed) the meter fills at the full
 * rate; at or above `hold.move_speed_u` (2.4 u/s, 30%) it drains at the full rate; between
 * them the two rates crossfade, so a player nudging the stick pays for the nudge in
 * proportion. Break-even sits at 1.2 u/s — 15% of top speed, 2.4 u of drift over a full
 * meter, less than the width of the ship's own contact circle.
 */
function meterRate(speed: number): number {
  const still = cfg.get('hold.still_speed_u');
  const move = cfg.get('hold.move_speed_u');
  const fill = 1 / cfg.get('hold.fill_s');
  const drain = fill * cfg.get('hold.drain_mult');
  const span = move - still;
  let t = span > 1e-9 ? (move - speed) / span : speed <= still ? 1 : 0;
  if (t > 1) t = 1;
  else if (t < 0) t = 0;
  return fill * t - drain * (1 - t);
}

// --- the ring ---

/**
 * §9.13.3 — summed threat weight inside the §5.8 acquisition ring. One `queryCircle` for the
 * whole sim, deduped through `w.seenStamp` so a body straddling several cells is counted once.
 *
 * Weighting reuses the director's own currency: `threat_score` is already elite-scaled
 * (`enemies.json` `elite_modifier.threat_cost` ×4 plus affix cost), so an elite gloomsquid
 * counts 4× a plain one without a second table. Bosses carry `threat_score = 0` by
 * construction (they are not spawned against the budget) and free spawns — bloomlings,
 * birthed fry, curtain bells — carry 0 as well, so both get an explicit weight instead.
 */
function scanRing(w: World, st: HoldState): void {
  const p = w.player;
  const rt = getPlayerRuntime(w);
  const ring = cfg.get('targeting.visible_range_u');
  // AMD-46 — scan out to the commitment radius, not just the acquisition ring. `ring` is
  // still where a body counts for its full weight; between the two it counts for the
  // fraction of the approach it has completed.
  let outer = cfg.get('hold.hunt_ring_u');
  if (outer < ring) outer = ring;
  const outerSpan = outer - ring;
  const n = w.hash.queryCircle(p.pos.x, p.pos.y, outer, ringBuf, ringBuf.length);
  const stamp = w.nextStamp();
  const bossW = cfg.get('hold.boss_threat');
  const minorW = cfg.get('hold.minor_threat');
  const closeRef = cfg.get('hold.close_ref_u');
  const contact = cfg.get('hold.contact_u');
  const band = cfg.get('hold.band_u');
  const bandSpan = band - contact;
  const r2 = outer * outer;

  // The player's own velocity, from the displacement the meter already measures, so a dash
  // and a knockback enter the closing rate the same way a hand on the stick does.
  const pvx = wrapDeltaX(p.pos.x - rt.prevX) / STEP;
  const pvy = wrapDeltaY(p.pos.y - rt.prevY) / STEP;

  let wPresence = 0;
  let wClosing = 0;
  let wBand = 0;
  let wArrive = 0;
  let count = 0;

  for (let i = 0; i < n; i++) {
    const idx = ringBuf[i];
    if (w.seenStamp[idx] === stamp) continue;
    w.seenStamp[idx] = stamp;
    const e = w.enemies.slots[idx];
    if (!e.alive || e.death_anim > 0) continue;
    const dx = wrapDeltaX(e.pos.x - p.pos.x);
    const dy = wrapDeltaY(e.pos.y - p.pos.y);
    const d2 = dx * dx + dy * dy;
    if (d2 > r2) continue;
    const d = Math.sqrt(d2);
    if (d <= ring) count++;
    const wgt = e.is_boss ? bossW : e.threat_score > 0 ? e.threat_score : minorW;
    if (d <= ring) wPresence += wgt;

    // How much of the approach this body has completed: 1 inside the acquisition ring,
    // fading to 0 at the commitment radius. A column still crossing 40 u is a real threat
    // to a ship that has chosen not to leave, and a smaller one than the same column at 20 u.
    const reach = d <= ring ? 1 : outerSpan > 1e-9 ? (outer - d) / outerSpan : 0;

    // ARRIVED: full weight inside the contact band, fading to nothing at `band_u`. A body
    // grinding on the hull has a closing rate of roughly zero and is the most dangerous
    // thing in the ring, so closing rate alone cannot be the whole measure.
    let prox = 0;
    if (d <= contact) prox = 1;
    else if (d < band && bandSpan > 1e-9) prox = (band - d) / bandSpan;
    wBand += wgt * prox;
    prox *= reach;

    // ARRIVING: closing speed along the player-relative vector, positive toward the ship.
    // A tail receding behind a fleeing player scores zero here; the same bodies walking in
    // on a planted player score their full archetype speed.
    let close = 0;
    if (d > 1e-6 && closeRef > 1e-9) {
      const rate = -((e.vel.x - pvx) * dx + (e.vel.y - pvy) * dy) / d;
      if (rate > 0) close = rate > closeRef ? 1 : rate / closeRef;
    }
    if (d <= ring) wClosing += wgt * close;
    close *= reach;

    let engage = prox > close ? prox : close;
    // ARRIVED AT ITS OWN RANGE (AMD-46). A ventspitter holding station at 15 u with a barb
    // in the wind-up has a closing rate of zero and is outside `band_u`, so both of the
    // measures above score it at nothing — and it is the single most common thing that kills
    // a planted ship. Measured: on seed 999 an anchored pilot died at 2:11 to a thin field of
    // two ranged bodies while its payout read 0.33, the reward and the danger pointing in
    // opposite directions. A body committed to an attack has arrived, whatever its velocity.
    if (e.attack_state === STATE_WINDING || e.attack_state === STATE_COMMITTED) engage = 1;

    wArrive += wgt * engage;
  }

  st.presence = wPresence;
  st.closing = wClosing;
  st.band = wBand;
  st.arrive = wArrive;
  st.count = count;
  st.weight = weightForMode(st, cfg.get('hold.threat_mode'));
  st.threat = threatFactor(st.weight);
}

/** §9.13.3 — which of the four measured weights the payout is derived from. */
export const enum ThreatMode {
  /** Everything present in the ring. The AMD-39 measure; kept for comparison. */
  Presence = 0,
  /** Weighted by closing speed only. */
  Closing = 1,
  /** Weighted by occupancy of the contact band only. */
  Band = 2,
  /** `max(band, closing)` — arrived or arriving. Shipped (AMD-40). */
  Arrive = 3,
}

function weightForMode(st: HoldState, mode: number): number {
  if (mode === ThreatMode.Presence) return st.presence;
  if (mode === ThreatMode.Closing) return st.closing;
  if (mode === ThreatMode.Band) return st.band;
  return st.arrive;
}

/** Diagnostic: all four candidate weights for the current tick (`tools/outrun-probe.ts`). */
export function holdWeightOfMode(w: World, mode: number): number {
  return weightForMode(stateOf(w), mode);
}

/**
 * §9.13.3 — weight → 0..1, on a saturating curve.
 *
 *     raw = w / (w + half)          threat = min(1, raw / (full / (full + half)))
 *
 * A linear ramp was measured first and rejected: the in-ring weight a live player sees
 * spans two orders of magnitude between minute 1 and minute 15, so any single linear
 * denominator either pays nothing for the first five minutes or saturates for the last
 * ten. The concave form pays a real fraction for a handful of bodies, still rewards a
 * genuine swarm, and keeps `hold.threat_full` as the honest weight at which the number
 * printed on the card is literally true. `half` → ∞ degenerates to the linear ramp, so
 * the shape stays sweepable from the registry.
 *
 * Zero bodies is exactly zero at every setting — the property the whole design rests on.
 */
export function threatFactor(weight: number): number {
  // A deadband first: one wanderer drifting through the ring is not a crowd, and the
  // concave curve is otherwise generous enough at the low end to pay for it.
  const w = weight - cfg.get('hold.threat_floor');
  if (w <= 0) return 0;
  const half = cfg.get('hold.threat_half');
  const full = cfg.get('hold.threat_full');
  const fullRaw = full / (full + half);
  if (fullRaw <= 1e-9) return 0;
  const norm = w / (w + half) / fullRaw;
  return norm > 1 ? 1 : norm;
}

// --- step 13a ---

/**
 * Called from the head of `stepProgression`. A build without the line early-outs before the
 * ring query and before any write to `player.stats`, so an undrafted run is bit-identical to
 * the pre-AMD-39 build — the same property that made AMD-24c's `fleeFactor` safe.
 */
export function stepHold(w: World): void {
  const st = stateOf(w);
  if (st.unsub === null) installHold(w);

  const rank = passiveRank(w, HOLD_PASSIVE_ID);
  st.rank = rank;
  if (rank <= 0) {
    if (st.meter !== 0 || st.payout !== 0 || st.engaged) {
      st.meter = 0;
      st.threat = 0;
      st.weight = 0;
      st.presence = 0;
      st.closing = 0;
      st.band = 0;
      st.arrive = 0;
      st.count = 0;
      st.payout = 0;
      st.engaged = false;
      clearBonus(st);
    }
    return;
  }

  const p = w.player;
  const rt = getPlayerRuntime(w);
  const wasEngaged = st.engaged;
  let dashed = false;

  if (!p.alive) {
    st.meter = 0;
  } else if (p.dash.active || rt.dashStartTick === w.tick) {
    // §9.13.4 — the dash is the escape valve and it costs the whole meter.
    dashed = st.meter > 0;
    st.meter = 0;
  } else {
    st.meter += meterRate(playerSpeedThisTick(w)) * STEP;
    if (st.meter > 1) st.meter = 1;
    else if (st.meter < 0) st.meter = 0;
  }

  scanRing(w, st);
  st.payout = st.meter * st.threat;
  st.engaged = st.meter >= 1 - ENGAGE_EPS;

  const row = RANKS[rank - 1];
  const b = st.bonus;
  const k = st.payout * payoutScale;
  b.damage_pct = row.damage_pct * k;
  b.attack_speed_pct = row.attack_speed_pct * k;
  b.crit_chance_pct = row.crit_chance_pct * k;
  b.armor_pct = row.armor_pct * k;
  b.mote_draw_mult = row.mote_draw_mult * k;
  b.ichor_pct = row.ichor_pct * k;

  applyHoldModifiers(w, st);

  if (dashed) emitHold(w, st, 'dashed');
  else if (st.engaged !== wasEngaged) emitHold(w, st, st.engaged ? 'anchored' : 'moved');
}

/**
 * Rewrites the four affected `PlayerStats` fields from the build's pre-hold base plus the
 * live bonus. Re-deriving from the base every tick (rather than adding to the current value)
 * is what makes the write idempotent: at `payout == 0` every field lands bit-identically on
 * what `recomputeStats` last wrote, because the §4.2 clamps are idempotent.
 */
function applyHoldModifiers(w: World, st: HoldState): void {
  const base = buildBaseOf(w);
  const s = w.player.stats;
  const b = st.bonus;

  let dmg = base.damage_add + b.damage_pct / 100;
  if (dmg > CAP_INCREASED_DAMAGE) dmg = CAP_INCREASED_DAMAGE;
  let haste = base.haste + b.attack_speed_pct / 100;
  if (haste > CAP_HASTE) haste = CAP_HASTE;
  let crit = base.crit_chance + b.crit_chance_pct / 100;
  if (crit > CAP_CRIT_CHANCE) crit = CAP_CRIT_CHANCE;
  let armor = base.armor + b.armor_pct / 100;
  if (armor > CAP_MITIGATION) armor = CAP_MITIGATION;

  s.damage_mult = 1 + dmg;
  s.cooldown_mult = 1 / (1 + haste);
  s.crit_chance = crit;
  s.armor = armor;
  s.pickup_radius_mult = base.pickup_radius_mult + b.mote_draw_mult;
  buildExtras.haste = haste;
}

// --- read API (§13 HUD, §12 audio, §11 VFX) ---

/** 0..1 meter fill. */
export function holdMeter(w: World): number {
  return stateOf(w).meter;
}

/** 0..1 threat factor inside the acquisition ring. */
export function holdThreat(w: World): number {
  return stateOf(w).threat;
}

/** Raw summed threat weight inside the ring (unnormalised). */
export function holdThreatWeight(w: World): number {
  return stateOf(w).weight;
}

/** Bodies inside the ring, deduped, dying excluded. */
export function holdThreatCount(w: World): number {
  return stateOf(w).count;
}

/** `meter × threat` — the scalar every reward axis multiplies. */
export function holdPayout(w: World): number {
  return stateOf(w).payout;
}

/** Owned rank, 0 when the line is not drafted. */
export function holdRank(w: World): number {
  return stateOf(w).rank;
}

/** True while the meter is full. */
export function holdEngaged(w: World): boolean {
  return stateOf(w).engaged;
}

/** The live bonus, already scaled by `payout`. Live object — read, never retain. */
export function holdBonuses(w: World): Readonly<HoldRank> {
  return stateOf(w).bonus;
}

/** Radius of the ring the threat is measured in (§5.8 `targeting.visible_range_u`). */
export function holdRingRadius(): number {
  return cfg.get('targeting.visible_range_u');
}

/** Radius out to which a body counts as committed, fading from full weight at the ring. */
export function holdCommitRadius(): number {
  const outer = cfg.get('hold.hunt_ring_u');
  const ring = cfg.get('targeting.visible_range_u');
  return outer < ring ? ring : outer;
}

/** Threat weight at which the payout saturates. */
export function holdThreatFull(): number {
  return cfg.get('hold.threat_full');
}

// --- §13.1.6 plain-language deltas ---

function pct(v: number): string {
  const r = Math.round(v * 10) / 10;
  return String(r) + '%';
}

/** The draw axis is a radius multiplier, so it prints as a multiple, not a percentage. */
function axisText(key: keyof HoldRank, v: number, label: string): string {
  if (key === 'mote_draw_mult') return '+' + String(Math.round(v * 10) / 10) + '× ' + label;
  return '+' + pct(v) + ' ' + label;
}

interface Axis {
  key: keyof HoldRank;
  up: string;
  down: string;
}

/** Fixed order so a delta string is stable across runs. */
const AXES: readonly Axis[] = [
  { key: 'damage_pct', up: 'damage', down: 'damage' },
  { key: 'attack_speed_pct', up: 'attack speed', down: 'attack speed' },
  { key: 'crit_chance_pct', up: 'critical chance', down: 'critical chance' },
  { key: 'armor_pct', up: 'less damage taken', down: 'less damage taken' },
  { key: 'mote_draw_mult', up: 'mote draw', down: 'mote draw' },
  { key: 'ichor_pct', up: 'more ichor per kill', down: 'more ichor per kill' },
];

function joinList(list: readonly string[]): string {
  if (list.length === 0) return '';
  if (list.length === 1) return list[0];
  let out = '';
  for (let i = 0; i < list.length - 1; i++) {
    if (i > 0) out += ', ';
    out += list[i];
  }
  return out + ' and ' + list[list.length - 1];
}

/** The condition clause every Holdfast string ends on, so the rule is never implicit. */
const CONDITION = ' — at a full anchor with the ring packed. Moving bleeds it, dashing sheds it.';

/** §13.1.6 — first acquisition. Never empty. */
export function holdNewDelta(rank: number): string {
  const row = holdRankValues(rank);
  const parts: string[] = [];
  for (let i = 0; i < AXES.length; i++) {
    const v = row[AXES[i].key];
    if (v > 0) parts.push(axisText(AXES[i].key, v, AXES[i].up));
  }
  let text = 'Ballast Holdfast — plant the ship with enemies inside your firing ring and gain ';
  text += parts.length > 0 ? joinList(parts) : 'nothing yet';
  text += CONDITION;
  if (rank > 1) text += ' Starts at rank ' + rank + '.';
  return text;
}

/** §13.1.6 — rank upgrade. Never empty; lists only the axes that moved. */
export function holdRankDelta(from: number, to: number): string {
  const a = holdRankValues(from);
  const b = holdRankValues(to);
  const head = 'Rank ' + from + ' → ' + to + ': ';
  const parts: string[] = [];
  for (let i = 0; i < AXES.length; i++) {
    const key = AXES[i].key;
    const va = a[key];
    const vb = b[key];
    if (va === vb) continue;
    const label = vb > va ? AXES[i].up : AXES[i].down;
    parts.push(
      va === 0
        ? axisText(key, vb, label) + ' (new)'
        : axisText(key, vb, label) + ' (was ' + axisText(key, va, '').trim() + ')'
    );
  }
  if (parts.length === 0) return head + 'holds the line harder' + CONDITION;
  return head + joinList(parts) + CONDITION;
}
